

import { canonicalDuplicateKeys } from "@/lib/guests/duplicateKeys";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useTransition,
  ReactNode,
} from "react";
import { useFilters } from "./FiltersProvider";
import { useDeepCompareMemo } from "../hooks/useDeepCompare";
import { useSignatureEffect } from "../hooks/useSignatureEffect";
import { getEventCount } from "@/lib/api/handlers/EventHandling";
import {
  countCompanions,
  getGuestsCount,
  getGuestsCountToBeReviewed,
} from "@/lib/api/handlers/GuestHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import { setPref } from "@/lib/storage/userPrefs";
import { parseFiltersApiRequest } from "@/lib/utils/parser";
import type { GroupScope } from "@/lib/guests/groupScope";
import { useFields } from "./FieldsProvider";
import { useLists } from "./ListsProvider";
import { countEmailSentDTOsByEvent } from "@/lib/api/handlers/EmailSentHandling";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";
import { getCheckInLogCount } from "@/lib/api/handlers/CheckInLogHandling";
import {
  getContactsCount,
  queryContactsCount,
} from "@/lib/api/handlers/ContactHandling";
import { getEventSpeakersCount } from "@/lib/api/handlers/EventSpeakerHandling";
import { getEventSponsorsCount } from "@/lib/api/handlers/EventSponsorHandling";
import {
  PARTY_GROUP_KEY,
  clearGroupBySuspension,
  isGroupByRejection,
  suspendGroupByForSession,
} from "@/lib/guests/groupBy";

// Define the context
type MetaTableContextType = {
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  sortOrder: "asc" | "desc";
  setSortOrder: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: (value: number) => void;
  archived: boolean;
  selectedRows: string[];
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  /** How the current selection is interpreted. `none` hides the bulk UI entirely. */
  selectionMode: "none" | "page" | "all-matching";
  setSelectionMode: React.Dispatch<React.SetStateAction<"none" | "page" | "all-matching">>;
  /** Rows the user un-ticked while `selectionMode === "all-matching"`. Ignored otherwise. */
  excludeIds: string[];
  setExcludeIds: React.Dispatch<React.SetStateAction<string[]>>;
  clearBulkSelection: () => void;
  isEditMode: boolean;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  type:
    | "event"
    | "guest"
    | "registrant"
    | "companion"
    | "emailSent"
    | "eventLimits"
    | "checkIn"
    | "actionLog"
    | "contact"
    | "speaker"
    | "sponsor";
  eventId?: string;
  setArchived: (value: boolean) => void;
  totalCount: number;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  /**
   * PB-TG-7 — the paging watermark this table's ROWS must also send, so that
   * the total and the pages describe the same set. `undefined` for every table
   * that does not use one. Stamped above the count effect.
   */
  pagingAsOf?: number;
  /**
   * Re-run the count round trip.
   *
   * <p>For callers that changed the row set and CANNOT correct the totals
   * themselves. The optimistic `setTotalCount(prev ± n)` arithmetic that every
   * delete/create lane uses is only valid while a row IS a person — in the
   * party-grouped guest list `totalCount` counts PARTIES and the derived
   * `selectionTotalCount` reads a separately fetched people number that no
   * mutation writes at all. Both go wrong, in opposite directions, and nothing
   * brings them back: the count signature carries no mutation token, and the
   * query invalidation runs with `refetchType: "none"`.
   */
  refreshCounts: () => void;
  loading: boolean;
  /**
   * The duplicate finder's composite key — canonical (sorted, unique) names
   * of built-in guest fields; a guest is a duplicate when ALL of them equal
   * another guest's. Read by the fetch and the banner; written only through
   * {@link enterDuplicateMode}.
   */
  duplicateFields: string[];
  /**
   * The ONE door into duplicate mode: stores the canonical key list, turns the
   * mode on and resets the page in the same commit. Re-running the check with
   * other keys while already in the mode goes through here too — a boolean
   * setter would see no change and leave the page where it was.
   */
  enterDuplicateMode: (fields: string[]) => void;
  isDuplicateMode: boolean;
  /**
   * Leaves duplicate mode (`false`); entering is {@link enterDuplicateMode}.
   * Kept as a setter for the exit paths (banner, dropdown) and tests.
   */
  setDuplicateMode: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * CRM transfer mode (guest list only): a contact-pool pane docks beside the
   * table for two-way transfers. Mutually exclusive with edit/duplicate mode —
   * the entry point clears those; exiting never touches them.
   */
  isCrmMode: boolean;
  setCrmMode: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Row grouping for the guest list. `""` = flat (today). `"mainGuest"` = one
   * row per PARTY, companions nested. Any other value is a column key whose
   * equal values arrive contiguously so the table can cut header rows.
   */
  groupBy: string;
  setGroupBy: (value: string) => void;
  /**
   * Drop the active grouping but KEEP the stored choice — for a refusal that
   * is expected to answer differently later. `setGroupBy("")` is the other
   * one: the user saying "none", which forgets it.
   */
  suspendGroupBy: () => void;
  /**
   * The count the SELECTION speaks in — always PEOPLE.
   *
   * <p>Equal to `totalCount` everywhere except party mode, where `totalCount`
   * becomes a count of parties so pagination can divide it by the page size.
   * The selection is a flat set of person ids either way: "select all matching"
   * means people, `excludeIds` holds people, and the banner counts people. Ask
   * `totalCount` for that number in party mode and the banner under-reports the
   * blast radius of a bulk action.
   */
  selectionTotalCount?: number;
  /**
   * The GROUP SCOPE of an all-matching selection, or null.
   *
   * "Select all N of group X" on the column-grouped guest list is the
   * all-matching selection the table already has, narrowed by one filter row
   * (`useBulkSelectionPayload` appends it). Every consumer that understands
   * all-matching therefore works unchanged; what changes is which people the
   * selection means, and `selectionTotalCount` says how many.
   *
   * Only ever non-null while `selectionMode === "all-matching"`.
   */
  groupScope: GroupScope | null;
  setGroupScope: React.Dispatch<React.SetStateAction<GroupScope | null>>;
  /** Enter a group scope: clears rows and excludes, flips to all-matching, sets the scope. */
  selectGroupScope: (scope: GroupScope) => void;
};

const MetaTableContext = createContext<MetaTableContextType | undefined>(
  undefined,
);

// `isAdvancedMode` lives on its own context so toggling it does not rebuild
// the (much larger) MetaTableContext value, which would force ~50 unrelated
// consumers (sort/page/filter/edit/etc.) to rerender. Consumers that need
// only the advanced-mode flag use `useAdvancedMode()` and skip rerenders
// driven by other state in the meta-table.
type AdvancedModeContextType = {
  isAdvancedMode: boolean;
  setAdvancedMode: React.Dispatch<React.SetStateAction<boolean>>;
};

const AdvancedModeContext = createContext<AdvancedModeContextType | undefined>(
  undefined,
);

// Provider component
/**
 * Does this table's COUNT get an `archived = false` clause pushed into it?
 *
 * Check-in logs, CRM contacts and speakers/sponsors have no archived field at
 * all — that was the original rule. `emailSent` joined the list on 2026-09-12,
 * for a sharper reason: its rows are MAILS, which are not archivable, so the
 * backend routes the key to the joined `guestData.archived` — and that clause
 * does not match a mail whose guest has since been deleted. The ROW query
 * injects nothing, so the count answered for a strictly smaller set than the
 * pages were drawn from: HeidiFest reported 1293 of 1315 and the pager stopped
 * 22 rows short of the end of the log. A total must describe the set its pages
 * come from.
 */
export function injectsArchivedFilter(type: string): boolean {
  return (
    type !== "checkIn" &&
    type !== "contact" &&
    type !== "speaker" &&
    type !== "sponsor" &&
    type !== "emailSent"
  );
}

export function MetaTableProvider({
  type,
  eventId,
  initialSortBy,
  initialSortOrder,
  initialPageSize,
  initialGroupBy = "",
  selectionTotalCountProps,
  archivedProp,
  children,
  totalCountProps,
  countAsOf,
  fetch = true,
  onCountUpdate,
  onCountError,
  contactCountParams,
}: {
  type:
    | "event"
    | "guest"
    | "registrant"
    | "companion"
    | "emailSent"
    | "eventLimits"
    | "checkIn"
    | "actionLog"
    // Company-CRM contact pool. `eventId` carries the COMPANY id on this
    // surface (scopes prefs/count-refresh events the same way an eventId does).
    | "contact"
    // Wave A — speaker/sponsor hub DataTables (event-scoped, no archived flag).
    | "speaker"
    | "sponsor";
  eventId?: string;
  initialSortBy: string;
  initialSortOrder: "asc" | "desc";
  initialPageSize: number;
  /**
   * Restored row grouping, already validated against the live field list by the
   * host page. Must be seeded HERE and not only set after mount: the host
   * prefetches the first page itself, so a grouping applied a beat later would
   * paint a flat list and then re-fetch a grouped one.
   */
  initialGroupBy?: string;
  /**
   * People, when that is a different number from `totalCountProps` (party
   * mode). Defaults to `totalCountProps` because everywhere else the two ARE
   * the same number.
   */
  selectionTotalCountProps?: number;
  archivedProp: boolean;
  children: ReactNode;
  totalCountProps: number;
  /**
   * PB-TG-7 — the paging watermark the ROWS are frozen at, for the tables that
   * use one (sent-mails today). The page seeds its own count with this instant;
   * this provider's count re-runs on every search / filter / page change and
   * did NOT send it, so after the first interaction the total described an
   * unbounded set while the rows stayed frozen, and the pager could offer a
   * page that renders empty — the exact drift the watermark exists to remove,
   * reintroduced one provider up. Optional: omitted means unbounded, i.e. the
   * behaviour of every other table.
   */
  countAsOf?: number;
  fetch?: boolean;
  /**
   * The count query re-runs for the ARCHIVE view too — its signature includes
   * `archived`. Consumers that mean "open items" must be able to tell the two
   * apart, or toggling the archive writes the archived total into a badge that
   * means something else entirely.
   */
  onCountUpdate?: (count: number, meta: { archived: boolean }) => void;
  /**
   * The count request failed. Previously only `console.error`, so a 403 or a
   * network blip left the last successful number on screen forever, silently
   * contradicting the table beneath it.
   */
  onCountError?: (meta: { archived: boolean }) => void;
  /**
   * `type === "contact"` only: extra filters for the count request. When set
   * (or when the active filter carries a non-empty condition tree) the count
   * routes through POST /contacts/query/count instead of the plain GET —
   * mirrors ContactsProvider's list-path switch so count and rows never
   * disagree. Plain pool surfaces omit it and stay on the GET.
   */
  contactCountParams?: { notInEventId?: string };
}) {
  const { allFieldsUnfiltered } = useFields();
  const listsProviderContext = useLists();
  const [duplicateFields, setDuplicateFields] = useState<string[]>(["email"]);
  const [isDuplicateMode, setDuplicateModeInHouse] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(initialSortOrder);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSizeInHouse] = useState<number>(initialPageSize);
  const [archived, setArchivedInHouse] = useState<boolean>(archivedProp);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] =
    useState<"none" | "page" | "all-matching">("none");
  const [excludeIds, setExcludeIds] = useState<string[]>([]);
  const [groupScope, setGroupScope] = useState<GroupScope | null>(null);
  // Read by callbacks and effects that must not re-bind on every scope change.
  const groupScopeRef = useRef<GroupScope | null>(null);
  groupScopeRef.current = groupScope;
  const [search, setSearch] = useState("");
  const [isEditMode, setEditModeInHouse] = useState<boolean>(false);
  const [isCrmMode, setCrmModeInHouse] = useState<boolean>(false);
  const [isAdvancedMode, setAdvancedMode] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const [totalCount, setTotalCount] = useState(totalCountProps);
  // PARTY MODE ONLY: how many PEOPLE the current filter matches.
  //
  // Seeded from the host prefetch. It is a second number only where the page
  // and the selection genuinely count different things; see
  // `selectionTotalCount` below, which is DERIVED rather than stored — holding
  // it was the bug: `setTotalCount` is written optimistically by a delete, a
  // create, duplicate mode and a broken filter, and none of them re-runs the
  // count effect, so a stored twin quietly kept quoting the old number on every
  // bulk affordance, on every table in the product.
  const [partyPeopleCount, setPartyPeopleCount] = useState(
    selectionTotalCountProps ?? totalCountProps,
  );
  const [groupBy, setGroupByInHouse] = useState<string>(initialGroupBy);
  const [countRefreshCounter, setCountRefreshCounter] = useState(0);

  /**
   * Ask the count effect to run again.
   *
   * Same lever the import / check-in / reconcile listeners below already pull;
   * exposed so a mutation that cannot do the arithmetic itself can say so
   * instead of guessing. `countSignature` carries this counter, so bumping it
   * is enough — no other dependency has to move.
   */
  const refreshCounts = useCallback(() => setCountRefreshCounter((c) => c + 1), []);

  // Company-wide surfaces mount WITHOUT an eventId (`eventLimits` on the
  // subscription page). Interpolating the missing id produced the literal key
  // `pageSize-eventLimits-undefined`, which no reader ever asks for — so that
  // table's size selector wrote a pref it could never restore. Drop the
  // segment instead of stringifying `undefined` into it.
  const prefScope = useCallback(
    () => (type === "event" ? "event" : eventId ? `${type}-${eventId}` : type),
    [type, eventId],
  );

  const getPageSizeCookieName = useCallback(
    () => `pageSize-${prefScope()}`,
    [prefScope],
  );

  // Persisted-sort pref keys, mirroring `getPageSizeCookieName`'s scoping.
  const getSortPrefKeys = useCallback(() => {
    const base = prefScope();
    return { by: `sort-${base}`, order: `sortOrder-${base}` };
  }, [prefScope]);

  // Generic here (every surface could grow a grouping), but the guest list
  // reads it back BEFORE this provider exists, from `groupByPrefKey`. The two
  // spellings are pinned against each other in
  // `src/lib/guests/__tests__/groupByPrefKey.test.ts` — a divergence would not
  // break loudly, the grouping would simply never be restored.
  const getGroupByPrefKey = useCallback(
    () => `groupBy-${prefScope()}`,
    [prefScope],
  );

  // Grouping changes what a ROW is, so it invalidates the page the user is on:
  // page 4 of a 300-person list is nowhere near page 4 of the same list grouped
  // into 90 parties. Reset with the change rather than after it, or the first
  // fetch asks for a page that does not exist and the table blinks empty.
  /**
   * A DELIBERATE choice: remember it, and un-park any session suspension.
   *
   * <p>The write lives here rather than in an effect on `groupBy`. As an
   * effect it could only fire when the VALUE changed, so picking "(None)" while
   * the grouping was already suspended to "" was a no-op — React bailed out,
   * the effect never ran, and the stored preference the user was trying to
   * clear survived. There was no other control: the picker reads "(None)"
   * after a refusal, so the one gesture that looks like "stop doing this" did
   * nothing at all.
   *
   * <p>It also separates the two ways `groupBy` becomes "": the user choosing
   * none (forget it) and a refusal parking it (keep it). See
   * {@link suspendGroupBy}.
   */
  const setGroupBy = useCallback(
    (value: string) => {
      setGroupByInHouse(value);
      setPage(1);
      setPref(getGroupByPrefKey(), value);
      if (value && eventId) clearGroupBySuspension(eventId);
    },
    [getGroupByPrefKey, eventId],
  );

  /**
   * Stand the grouping down WITHOUT forgetting it.
   *
   * <p>For a transient refusal — the party cap, which moves with every import,
   * archive and filter change. The rows have to become ungrouped now, but the
   * choice is still a good one and comes back as soon as the list is narrowed.
   * Clearing the preference here (which is what `setGroupBy("")` does) was the
   * provider contradicting the page, which deliberately preserves it.
   */
  const suspendGroupBy = useCallback(() => {
    setGroupByInHouse("");
    setPage(1);
  }, []);

  // Persist the active sort so it survives a reload. Skip the very first run:
  // on mount `sortBy`/`sortOrder` are the (already visibility-gated) initial
  // values, and writing them back would clobber a still-good stored sort whose
  // column happens to be hidden in the current view. Only user-driven changes
  // (which re-run this effect after mount) get persisted. The read-back +
  // visibility gating lives at the guest-list page (`readInitialSort`).
  const sortPersistInit = useRef(true);
  useEffect(() => {
    if (sortPersistInit.current) {
      sortPersistInit.current = false;
      return;
    }
    const { by, order } = getSortPrefKeys();
    setPref(by, sortBy);
    setPref(order, sortOrder);
  }, [sortBy, sortOrder, getSortPrefKeys]);

  const setArchived = useCallback((value: boolean) => {
    setArchivedInHouse(value);
  }, []);

  const setPageSize = useCallback(
    (value: number) => {
      setPageSizeInHouse(value);
      setPref(getPageSizeCookieName(), value.toString());
    },
    [getPageSizeCookieName],
  );

  const { activeFilterState } = useFilters();
  const stableFilters = useDeepCompareMemo(activeFilterState?.filter);
  const stableConditions = useDeepCompareMemo(activeFilterState?.conditions);

  // Any change to the filter set (filters / search / archived toggle) invalidates the
  // entire selection context and resets both page- and all-matching-mode state.
  const filterContextResetRef = useRef(true);
  useEffect(() => {
    if (filterContextResetRef.current) { filterContextResetRef.current = false; return; }
    setSelectedRows([]);
    setSelectionMode("none");
    setExcludeIds([]);
    // A group scope is a filter-wide selection under THIS filter; the group's
    // count and membership do not survive a change of it.
    setGroupScope(null);
  }, [search, stableFilters, stableConditions, archived]);

  /**
   * Grouping is SUSPENDED while the table is in duplicate or CRM mode — the
   * value the rest of the app reads, and the value that reaches the request.
   *
   * Those two modes act on a flat list of rows: duplicate mode pairs rows, CRM
   * mode drags a row into the contact pool. A party head standing in for three
   * people is not one of those rows, and the control that sets grouping is
   * disabled in both — but the STORED choice would otherwise keep travelling
   * to the server and keep the list grouped underneath a mode that cannot
   * handle it.
   *
   * Inline edit is deliberately NOT on that list any more. It used to be, and
   * that suspension was the whole defect: entering edit mode changed this
   * value, the effect below read that as a page change and wiped the page-mode
   * selection (nothing left to edit), or — with nothing ticked — the dropdown
   * seeded the GROUPED page's ids and the list then refetched FLAT under them,
   * so only the rows that happened to sit on both pages became editable. Edit
   * mode edits the rows as they are rendered: a party stays a party, a column
   * header stays a header, and the row you ticked is the row you edit. The
   * companion rows that made the flat list look necessary are reachable now
   * (`GuestsProvider` walks the party tree, see `partyTree.ts`).
   *
   * The user's choice is not thrown away: the state (and its pref) keep it, so
   * leaving duplicate / CRM mode restores the grouping.
   */
  const effectiveGroupBy = isDuplicateMode || isCrmMode ? "" : groupBy;

  /**
   * The number every bulk affordance speaks in — always PEOPLE.
   *
   * DERIVED, never stored. Outside the party-grouped guest list it IS
   * `totalCount`, so it follows every optimistic write to it for free; only
   * party mode, where the page counts parties and the selection counts people,
   * needs the separately fetched number.
   */
  const selectionTotalCount = groupScope
    ? // A scoped all-matching selection is the GROUP: its header count, kept
      // fresh by the group-counts query while the scope lives.
      groupScope.count
    : type === "guest" && effectiveGroupBy === PARTY_GROUP_KEY
      ? partyPeopleCount
      : totalCount;

  // Page / pageSize / sort changes only invalidate `page`-mode selections (selectedRows
  // reference specific IDs on a specific page). `all-matching` mode must persist so that
  // a user who excluded five rows on page 1 and paginates to browse still has those
  // excludes applied when the bulk action fires.
  const paginationResetRef = useRef(true);
  // What the LAST render's rows were grouped by. `setGroupBy` resets the page
  // itself, but the suspension (duplicate / CRM mode) changes the effective
  // grouping without going through it — and a page number does not survive
  // that change: party page 3 of 3 becomes people page 3 of 10 and back
  // again, so leaving the mode could land on a page the grouped list no
  // longer has. The table then renders empty with a footer that disagrees.
  // Inline edit does not change the effective grouping, so it neither resets
  // the page nor touches the selection here — that is the point of keeping
  // the grouping on during an edit.
  const lastEffectiveGroupByRef = useRef(effectiveGroupBy);
  useEffect(() => {
    if (paginationResetRef.current) { paginationResetRef.current = false; return; }
    if (lastEffectiveGroupByRef.current !== effectiveGroupBy) {
      lastEffectiveGroupByRef.current = effectiveGroupBy;
      setPage(1);
      // A group scope names a group of the grouping that is GONE now — the
      // column changed, "(None)" was picked, the count lane parked it, or
      // duplicate / CRM mode suspended it. The WHOLE selection goes with it,
      // not just the scope: an all-matching selection left standing without
      // its scope row would silently widen from "3 of Acme" to "all 40
      // matching", which is exactly the number the CRM banner would then
      // offer to transfer.
      if (groupScopeRef.current) {
        setSelectedRows([]);
        setSelectionMode("none");
        setExcludeIds([]);
        setGroupScope(null);
      }
    }
    if (selectionMode === "page") {
      setSelectedRows([]);
      setSelectionMode("none");
    }
    // selectionMode === "all-matching" intentionally preserved across pagination + sort.
    // `groupBy` rides here for the same reason as sort: the ids on the page
    // change, so a page-mode selection no longer refers to what is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, effectiveGroupBy]);


  const clearBulkSelection = useCallback(() => {
    setSelectedRows([]);
    setSelectionMode("none");
    setExcludeIds([]);
    setGroupScope(null);
  }, []);

  /**
   * Enter a group scope in ONE commit: rows and excludes cleared, mode
   * all-matching, scope set. Four setters inside an event handler are one
   * React batch, and nothing keys on the transitional states — but the
   * invariant "scope ⇒ all-matching" is easier to trust when there is exactly
   * one door.
   */
  const selectGroupScope = useCallback((scope: GroupScope) => {
    setSelectedRows([]);
    setExcludeIds([]);
    setSelectionMode("all-matching");
    setGroupScope(scope);
  }, []);

  /**
   * The mode setters, wrapped so ENTERING a mode clears a group scope in the
   * same commit — not one commit later in the pagination effect. In between,
   * the rows would already render the mode's chrome (CRM drag grips, say)
   * against an all-matching selection whose scope row nobody in that mode
   * reads: the drag payload counts `totalCount`, the transfer resolver knows
   * no scope. Unreachable by a human within one frame, but an invariant that
   * rests on effect timing is an invariant waiting to be broken.
   */
  const modesRef = useRef({ edit: false, crm: false, dup: false });
  modesRef.current = { edit: isEditMode, crm: isCrmMode, dup: isDuplicateMode };
  const clearScopedSelection = useCallback(() => {
    if (!groupScopeRef.current) return;
    setSelectedRows([]);
    setSelectionMode("none");
    setExcludeIds([]);
    setGroupScope(null);
  }, []);
  const wrapModeSetter = useCallback(
    (
      setInHouse: React.Dispatch<React.SetStateAction<boolean>>,
      read: () => boolean,
    ): React.Dispatch<React.SetStateAction<boolean>> =>
      (next) => {
        const entering = typeof next === "function" ? next(read()) : next;
        if (entering) clearScopedSelection();
        setInHouse(next);
      },
    [clearScopedSelection],
  );
  const setEditMode = useMemo(
    () => wrapModeSetter(setEditModeInHouse, () => modesRef.current.edit),
    [wrapModeSetter],
  );
  const setCrmMode = useMemo(
    () => wrapModeSetter(setCrmModeInHouse, () => modesRef.current.crm),
    [wrapModeSetter],
  );
  // Duplicate mode changes the whole dataset (the rows are a different list,
  // the total a different number), so the page cannot survive a toggle in
  // either direction: guest page 3 is not duplicates page 3, and duplicates
  // page 2 is not guest page 2. The effectiveGroupBy effect below resets it
  // only when a GROUPING was suspended — an ungrouped list kept the page.
  const setDuplicateModeBase = useMemo(
    () => wrapModeSetter(setDuplicateModeInHouse, () => modesRef.current.dup),
    [wrapModeSetter],
  );
  const setDuplicateMode = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (next) => {
      const was = modesRef.current.dup;
      const value = typeof next === "function" ? next(was) : next;
      setDuplicateModeBase(value);
      if (value !== was) setPage(1);
    },
    [setDuplicateModeBase],
  );
  const enterDuplicateMode = useCallback(
    (fields: string[]) => {
      // Canonical at the boundary: [a,b] and [b,a] are one request, and the
      // request signature that dedupes fetches compares this array.
      setDuplicateFields(canonicalDuplicateKeys(fields));
      // The selection is cleared HERE, where the mode starts — not when the
      // dialog opens. It names rows of the FULL list, and the duplicates list
      // is a different dataset (an all-matching selection would silently come
      // to mean "all duplicates"). Clearing it at dialog-open time is what
      // unmounted the dropdown, and with it the dialog (see GuestsDropDown).
      clearBulkSelection();
      // Through the mode setter (clears a group scope on entry), then the
      // page — unconditionally: re-running the check with other keys while
      // already in the mode is a new dataset too.
      setDuplicateModeBase(true);
      setPage(1);
    },
    [clearBulkSelection, setDuplicateModeBase],
  );

  // Auto-refresh count when a background import job completes
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!eventId || detail?.eventId === eventId) {
        setCountRefreshCounter((c) => c + 1);
      }
    };
    window.addEventListener("importGuestsCompleted", handler);
    return () => window.removeEventListener("importGuestsCompleted", handler);
  }, [eventId]);

  useEffect(() => {
    if (type !== "checkIn") return;
    const handler = () => setCountRefreshCounter((c) => c + 1);
    window.addEventListener("checkInActionCompleted", handler);
    return () => window.removeEventListener("checkInActionCompleted", handler);
  }, [type]);

  // An automation moves rows too, and it does it without anybody clicking.
  //
  // The rows refetch on a reconcile; the count did not, because its signature
  // only changes with filters/search/page. So a rule that moved a guest out of
  // the active filter left the grid showing 24 rows under a footer saying 25,
  // and offered a page two with nothing on it — until the operator happened to
  // touch a filter. Same shape as the import/check-in handlers above.
  useEffect(() => {
    // Guest surfaces only. The sibling handler above gates on `type`; this one
    // did not, so an events overview or a subscription page re-counted on every
    // guest reconcile in any event.
    if (type !== "guest" && type !== "registrant" && type !== "companion") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { eventId?: string } | null;
      // Match on the id, and require both sides to have one. The two fail-open
      // clauses this replaces (`!eventId ||`, `!detail?.eventId ||`) each turned
      // a missing id into "counts as a match": company-wide surfaces mount
      // WITHOUT an eventId — `eventLimits` on the subscription page, documented
      // at the top of this provider — so the first clause made every guest
      // reconcile anywhere re-count that table. The second was not yet live
      // (both senders do attach an eventId) but was a trapdoor for the next
      // sender that forgets.
      if (eventId && detail?.eventId === eventId) {
        setCountRefreshCounter((c) => c + 1);
      }
    };
    window.addEventListener("guestReconciled", handler);
    window.addEventListener("guestReconcileCoarse", handler);
    return () => {
      window.removeEventListener("guestReconciled", handler);
      window.removeEventListener("guestReconcileCoarse", handler);
    };
  }, [eventId, type]);

  // Deduped by request signature (mirrors what the count request sends), not a
  // first-run boolean: the ref is seeded with the mount signature because the
  // host already fetched that exact count and passed it as `totalCountProps` —
  // and StrictMode's dev effect replay used to slip past the old
  // `initialized` guard and double-fire the count endpoint on every mount.
  const countSignature = JSON.stringify({
    type,
    eventId,
    archived,
    search,
    filters: stableFilters ?? null,
    conditions: stableConditions ?? null,
    whitelist: listsProviderContext?.whitelist ?? null,
    blacklist: listsProviderContext?.blacklist ?? null,
    allRecipients: listsProviderContext?.allRecipients ?? null,
    contactNotInEventId: contactCountParams?.notInEventId ?? null,
    groupBy: effectiveGroupBy || null,
    countRefreshCounter,
    // Duplicate mode owns the total while it is on (the duplicates fetch
    // writes it); the count effect must run again when it goes OFF, or the
    // footer keeps the duplicates total over the full list until the next
    // filter change. Entering runs the effect too — it early-returns, and
    // the token bump invalidates an in-flight list count that could
    // otherwise land after the duplicates fetch and overwrite ITS total.
    dup: isDuplicateMode,
  });

  // PB-TG-7 — ONE paging watermark for the rows and the total.
  //
  // It has to live here, above both queries. The page used to stamp its own
  // instant keyed on the URL filter, while the row provider stamped a second
  // one keyed on the full query (search, filters, sort, page size). Those two
  // keys move at different times: after the first in-table search the ROWS were
  // stamped later than the COUNT, the count then described a strictly smaller
  // set than the pages were drawn from, and `Math.ceil(total / pageSize)` hid
  // the tail of the log. That is the same class of defect as the archived
  // clause above — a total that does not describe its own pages — reintroduced
  // by a fix for the other half of it.
  //
  // Stamped per COUNT SIGNATURE, i.e. per SET: it stays fixed while the user
  // walks pages and resets when they change what they are looking at. The
  // page's own seed instant is honoured for the first signature so the seeded
  // total and the seeded rows agree on their first paint. Held in a ref, so
  // taking a stamp never itself triggers a fetch.
  const asOfRef = useRef<{ signature: string; at: number } | null>(null);
  if (countAsOf !== undefined && asOfRef.current?.signature !== countSignature) {
    asOfRef.current = {
      signature: countSignature,
      at: asOfRef.current === null ? countAsOf : Date.now(),
    };
  }
  const pagingAsOf = countAsOf === undefined ? undefined : asOfRef.current?.at;
  // Same monotonic guard as GuestsProvider's row fetch: the count effect has no
  // cancellation either, so a slower earlier count (e.g. the unfiltered one that
  // was in flight when the user hit Enter in the search box) could land last and
  // overwrite the narrowed total — pagination then offers pages the row query
  // has no rows for.
  const countTokenRef = useRef(0);
  useSignatureEffect(countSignature, () => {
    if (!fetch) return;

    const token = ++countTokenRef.current;
    const isStale = () => token !== countTokenRef.current;

    startTransition(async () => {
      try {
        // Duplicate mode: the duplicates fetch sets the total itself.
        if (isDuplicateMode) {
          return;
        }
        let filtersCopy = !activeFilterState?.filter
          ? []
          : [...activeFilterState?.filter]; // Create a copy of the array

        if (injectsArchivedFilter(type)) {
          if (archived) {
            filtersCopy.push({
              key: "archived",
              operator: "=",
              value: "true",
            });
          } else {
            filtersCopy.push({
              key: "archived",
              operator: "=",
              value: "false",
            });
          }
        }

        // allFieldsUnfiltered: don't drop a CUSTOM-role's saved filter on an
        // allowlist-hidden field (would broaden the count). See GuestsProvider.
        filtersCopy = parseFiltersApiRequest(filtersCopy, allFieldsUnfiltered || []);

        let count;
        if (type === "checkIn") {
          count = await getCheckInLogCount(eventId!, filtersCopy, search);
        } else if (type === "event" || type === "eventLimits") {
          count = await getEventCount(archived, filtersCopy, search);
        } else if (type === "guest") {
          // Email-recipient explicit-selection mode (mail-templates send): the
          // recipient set is strictly the whitelist (empty = nobody), so the
          // table/pagination count must follow that — not the filter total.
          if (
            listsProviderContext &&
            listsProviderContext.allRecipients === false
          ) {
            if ((listsProviderContext.whitelist?.length ?? 0) === 0) {
              count = 0;
            } else {
              count = await getGuestsCount(
                false,
                [],
                eventId!,
                "",
                listsProviderContext.whitelist,
                [],
              );
            }
          } else {
            // Conditions mode: the active filter is a nested tree (word
            // operators, real OR). Send it instead of the flat rows — an OR
            // tree's flat derivation is empty and would over-count.
            const conditions = activeFilterState?.conditions;
            const conditionsMode =
              !!conditions && hasNonEmptyConditions(conditions);
            // Party mode needs BOTH numbers and they answer different
            // questions: parties divide into pages, people are what a bulk
            // action touches. Deriving the second from the first is impossible
            // (party sizes vary) and guessing it is how a banner ends up
            // under-reporting a send.
            //
            // In PARALLEL: they are independent, and the party count is the
            // expensive one — awaiting it before even asking for the cheap
            // people count left the banner and the footer a full round trip
            // behind the rows on exactly the events where that hurts.
            const askCount = (groupBy?: string) =>
              getGuestsCount(
                archived,
                conditionsMode ? [] : filtersCopy,
                eventId!,
                search,
                listsProviderContext?.whitelist,
                listsProviderContext?.blacklist,
                undefined,
                conditionsMode ? conditions : undefined,
                groupBy,
              );
            if (effectiveGroupBy === PARTY_GROUP_KEY) {
              const [parties, people] = await Promise.all([
                askCount(PARTY_GROUP_KEY),
                askCount(undefined),
              ]);
              if (isStale()) return;
              if (typeof people === "number") setPartyPeopleCount(people);
              count = parties;
            } else {
              count = await askCount(undefined);
            }
          }
        } else if (type === "registrant") {
          count = await getGuestsCountToBeReviewed(
            archived,
            filtersCopy,
            eventId!,
            search,
          );
        } else if (type === "emailSent") {
          count = await countEmailSentDTOsByEvent(
            eventId!,
            filtersCopy,
            search,
            pagingAsOf,
          );
        } else if (type === "speaker" || type === "sponsor") {
          // Wave A hub tables — EXPLICIT branches to the new count endpoints
          // (the documented silent-GUEST-fallback trap: an else-bucket here
          // would count companions). Conditions mode mirrors the guest path:
          // an OR tree's flat derivation is empty and would over-count.
          const entityConditions = activeFilterState?.conditions;
          const entityConditionsMode =
            !!entityConditions && hasNonEmptyConditions(entityConditions);
          count =
            type === "speaker"
              ? await getEventSpeakersCount(
                  eventId!,
                  entityConditionsMode ? [] : filtersCopy,
                  search,
                  entityConditionsMode ? entityConditions : undefined,
                )
              : await getEventSponsorsCount(
                  eventId!,
                  entityConditionsMode ? [] : filtersCopy,
                  search,
                  entityConditionsMode ? entityConditions : undefined,
                );
        } else if (type === "contact") {
          // Company-scoped (JWT) — no eventId in the request. Conditions
          // (CRM-pane filter tree) and notInEventId (hide-in-event toggle)
          // need the POST query/count; the plain pool count stays on the GET
          // (search only — contacts have no flat filters/archived in v1).
          const contactConditions = activeFilterState?.conditions;
          const contactConditionsMode =
            !!contactConditions && hasNonEmptyConditions(contactConditions);
          const notInEventId = contactCountParams?.notInEventId;
          count =
            contactConditionsMode || notInEventId
              ? await queryContactsCount({
                  searchTerm: search,
                  ...(contactConditionsMode && {
                    conditions: contactConditions,
                  }),
                  ...(notInEventId && { notInEventId }),
                })
              : await getContactsCount({ searchTerm: search });
        } else {
          count = await countCompanions(eventId!, filtersCopy, archived, search);
        }

        if (
          typeof count === "object" &&
          "isServerError" in count &&
          count.isServerError === true
        )
          throwServerError(count);

        if (isStale()) return;

        if (onCountUpdate) onCountUpdate(count as number, { archived });
        setTotalCount(count as number);

      } catch (error) {
        console.error("Error fetching data:", error);
        if (isStale()) return;
        // A grouping refused by the COUNT lane. Reached when the party set
        // crosses the cap AFTER mount — an import, a widened filter — where the
        // list lane may not re-run at all. Without this branch the grouping
        // stayed active over a denominator that never arrived, and nothing was
        // said; `onCountError` is not even wired by the guest list.
        //
        // Same rule as the two other lanes: the cap is transient (keep the
        // choice, park it for this tab), a refused column is not.
        if (error instanceof Error && isGroupByRejection(error.message)) {
          if (error.message.startsWith("groupTooLarge")) {
            if (eventId) suspendGroupByForSession(eventId);
            suspendGroupBy();
          } else {
            setGroupBy("");
          }
          return;
        }
        if (onCountError) onCountError({ archived });
      }
    });
  });

  const contextValue = useMemo(() => ({
    sortBy, setSortBy, sortOrder, setSortOrder, page, setPage, pageSize, setPageSize,
    archived, type, eventId, selectedRows, setSelectedRows,
    selectionMode, setSelectionMode, excludeIds, setExcludeIds, clearBulkSelection,
    isEditMode, setEditMode, isCrmMode, setCrmMode,
    setArchived, search, setSearch, totalCount, pagingAsOf,
    setTotalCount, refreshCounts, loading: isPending, duplicateFields, enterDuplicateMode, isDuplicateMode, setDuplicateMode,
    groupBy: effectiveGroupBy, setGroupBy, suspendGroupBy, selectionTotalCount,
    groupScope, setGroupScope, selectGroupScope,
  }), [sortBy, setSortBy, sortOrder, setSortOrder, page, setPage, pageSize, setPageSize,
    archived, type, eventId, selectedRows, setSelectedRows,
    selectionMode, setSelectionMode, excludeIds, setExcludeIds, clearBulkSelection,
    isEditMode, setEditMode, isCrmMode, setCrmMode,
    setArchived, search, setSearch, totalCount,
    setTotalCount, refreshCounts, isPending, duplicateFields, enterDuplicateMode, isDuplicateMode, setDuplicateMode,
    effectiveGroupBy, setGroupBy, suspendGroupBy, selectionTotalCount,
    groupScope, selectGroupScope, pagingAsOf]);

  // Advanced-mode context value is intentionally tiny and stable.
  //
  // It deliberately does NOT clear `search`: `useTableSearchBox` owns that
  // transition and pairs the clear with `setPage(1)`. Clearing here landed the
  // term and the mode in the same commit, so the hook's `if (search)` guard was
  // already false and the page reset never ran — a collapse from page 3 stayed
  // on page 3 of the widened list.
  const advancedModeValue = useMemo(
    () => ({ isAdvancedMode, setAdvancedMode }),
    [isAdvancedMode],
  );

  return (
    <AdvancedModeContext.Provider value={advancedModeValue}>
      <MetaTableContext.Provider value={contextValue}>
        {children}
      </MetaTableContext.Provider>
    </AdvancedModeContext.Provider>
  );
}

export function useAdvancedMode() {
  const context = useContext(AdvancedModeContext);
  if (!context) {
    throw new Error(
      "useAdvancedMode must be used within a MetaTableProvider",
    );
  }
  return context;
}

/** Optional variant for shared UI that may render outside a MetaTableProvider. */
export function useAdvancedModeOptional() {
  return useContext(AdvancedModeContext);
}

// Hook for consuming the context
export function useMetaTable() {
  const context = useContext(MetaTableContext);
  if (!context) {
    throw new Error("useMetaTable must be used within a MetaTableProvider");
  }
  return context;
}

/**
 * Optional variant — returns `undefined` when rendered outside a MetaTableProvider.
 * Useful from shared modals (e.g. `PSendEmail`) that are reused both inside and outside
 * table contexts.
 */
export function useMetaTableOptional() {
  return useContext(MetaTableContext);
}
